/**
 * Access-token refresh loop.
 *
 * The access token lives ~15 min (Federate `expires_in`) — much shorter than the
 * id_token — and is renewed through a different channel: `POST /auth/refresh`
 * (refresh_token grant), which does NOT re-issue an id_token. So it gets its own
 * loop, independent of the id_token's hidden-iframe code-flow refresh. Every
 * mint still goes through the Gateway, which persists the new access token to
 * AgentUserToken so server-side readers (runtime, awsentral OBO) stay live.
 *
 * Shape: one piece of state (`expMs`) and one timer.
 *
 *   storeAccessToken()  — any mint path (login, silent code flow, this loop)
 *                         calls it; it writes the cookie and (re)arms the timer.
 *   start()             — provider calls it while an id_token session exists;
 *                         returns a stop function.
 *   tick()              — fires at exp − 5 min:
 *                           ok           → storeAccessToken re-armed us
 *                           401          → stay idle until the next mint
 *                           other/error  → retry in 60 s
 */

import { FEDERATE } from '../config';
import { decodeJwt, setCookie } from './authStorage';

/** Refresh this long before the token expires. */
const LEAD_MS = 5 * 60_000;
/** Never fire sooner than this (also the delay when expiry is unknown, e.g. after a reload). */
const MIN_DELAY_MS = 30_000;
/** Back-off after a transient failure. */
const RETRY_MS = 60_000;
const FETCH_TIMEOUT_MS = 10_000;
/** Lifetime to assume when the response carries neither a JWT `exp` nor `expires_in`. */
const DEFAULT_TTL_MS = 15 * 60_000;

let expMs = 0; // last known expiry; 0 = unknown
let timer: ReturnType<typeof setTimeout> | undefined;
let running = false;

/** Write a freshly minted access token to its cookie and re-arm the loop. */
export function storeAccessToken(accessToken: string, expiresIn?: number): void {
  expMs = expiryOf(accessToken, expiresIn);
  setCookie(FEDERATE.accessTokenKey, accessToken, expMs);
  if (running) arm(expMs - LEAD_MS - Date.now());
}

/** Run the loop until the returned stop function is called. */
export function startAccessTokenLoop(): () => void {
  running = true;
  arm(expMs > 0 ? expMs - LEAD_MS - Date.now() : 0);
  return () => {
    running = false;
    clearTimeout(timer);
  };
}

/** Forget the tracked expiry (logout). */
export function resetAccessTokenLoop(): void {
  expMs = 0;
}

function arm(delayMs: number): void {
  clearTimeout(timer);
  const delay = Math.max(delayMs, MIN_DELAY_MS);
  console.log(`[auth] Access refresh scheduled in ${Math.round(delay / 1000)}s`);
  timer = setTimeout(tick, delay);
}

async function tick(): Promise<void> {
  const result = await refreshOnce();
  if (!running) return;
  if (result === 'retry') arm(RETRY_MS);
  // 'ok': storeAccessToken already re-armed. 'unauthorized': the refresh cookie
  // is gone — idle until login / silent refresh mints again via storeAccessToken.
}

async function refreshOnce(): Promise<'ok' | 'unauthorized' | 'retry'> {
  try {
    const resp = await fetch(FEDERATE.refreshEndpoint, {
      method: 'POST',
      credentials: 'include',
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    if (resp.status === 401) {
      console.warn('[auth] Access refresh: no refresh session');
      return 'unauthorized';
    }
    if (!resp.ok) {
      console.warn('[auth] Access refresh rejected:', resp.status);
      return 'retry';
    }
    const data = await resp.json();
    if (typeof data.access_token !== 'string') return 'retry';
    storeAccessToken(data.access_token, data.expires_in);
    console.log('[auth] Access refresh succeeded');
    return 'ok';
  } catch (e) {
    console.warn('[auth] Access refresh error:', e);
    return 'retry';
  }
}

/** JWT `exp` if decodable, else now + expires_in, else now + default TTL. */
function expiryOf(accessToken: string, expiresIn?: number): number {
  try {
    const exp = decodeJwt(accessToken).exp;
    if (typeof exp === 'number' && exp > 0) return exp * 1000;
  } catch {
    /* opaque token */
  }
  return Date.now() + (expiresIn && expiresIn > 0 ? expiresIn * 1000 : DEFAULT_TTL_MS);
}
